"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHomePurchasePriceSliderBounds = exports.scaleHomePurchaseTermsToSelectedPrice = exports.sumFeeDollarsByFinancingOption = exports.scaleAmountToSelectedOffer = exports.getHomePurchaseDownPaymentFraction = exports.getCalculatedAmountDollars = void 0;
const assert_1 = __importDefault(require("assert"));
const lodash_1 = require("lodash");
const assertUtils_1 = require("../assertUtils");
const homePurchaseTerms_1 = require("./homePurchaseTerms");
/**
 * Shared FLP Aven Advantage slider math: the backend letter and the frontend page scale the offer's
 * homePurchaseTerms the same way. Every amount is rounded in integer cents before being restated as dollars —
 * aven_types can't use the backend's mathCore addon, and cents products stay under MAX_SAFE_INTEGER for FLP
 * prices, so results are exact to the cent.
 */
const dollarsToCents = (dollars) => Math.round(dollars * 100);
const centsToDollars = (cents) => cents / 100;
const getCalculatedAmountDollars = (amountTerms) => {
    switch (amountTerms.method) {
        case homePurchaseTerms_1.CalculationMethod.fractionOfLineSize:
            return amountTerms.amountAssumingOfferLineSize;
        case homePurchaseTerms_1.CalculationMethod.fractionOfPurchasePrice:
            return amountTerms.amountAssumingOfferPurchasePrice;
        case homePurchaseTerms_1.CalculationMethod.fractionOfAvenPurchaseShare:
            return amountTerms.amountAssumingOfferAvenPurchaseShare;
        case homePurchaseTerms_1.CalculationMethod.fixedAmount:
            return amountTerms.fixedAmount;
        default:
            return (0, assertUtils_1.assertNever)(amountTerms, `Unhandled CalculationMethod ${JSON.stringify(amountTerms)}`);
    }
};
exports.getCalculatedAmountDollars = getCalculatedAmountDollars;
// null for a fixed-dollar down payment, which has no fraction to quote.
const getHomePurchaseDownPaymentFraction = (downPaymentTerms) => {
    switch (downPaymentTerms.method) {
        case homePurchaseTerms_1.CalculationMethod.fractionOfLineSize:
            return { fraction: downPaymentTerms.fractionOfLineSize, method: downPaymentTerms.method };
        case homePurchaseTerms_1.CalculationMethod.fractionOfPurchasePrice:
            return { fraction: downPaymentTerms.fractionOfPurchasePrice, method: downPaymentTerms.method };
        case homePurchaseTerms_1.CalculationMethod.fractionOfAvenPurchaseShare:
            return { fraction: downPaymentTerms.fractionOfAvenPurchaseShare, method: downPaymentTerms.method };
        case homePurchaseTerms_1.CalculationMethod.fixedAmount:
            return null;
    }
};
exports.getHomePurchaseDownPaymentFraction = getHomePurchaseDownPaymentFraction;
/**
 * Restates one dollar amount — a down payment, a fee, anything quoted as a CalculatedAmount — against a new
 * set of offer figures. The method names which figure binds the amount, so the dollars are recomputed from
 * that figure inside each case rather than from a single price ratio outside the switch: a fractionOfLineSize
 * amount tracks line size, a fractionOfPurchasePrice amount tracks price, and a fixedAmount never moves.
 * The fraction itself is the term of record and carries through untouched.
 *
 * When the slider grows to move the down payment as well as the price, the caller computes the new figures
 * from both selections and this function keeps working unchanged.
 */
const scaleAmountToSelectedOffer = ({ amountToScale, originalOfferSize, selectedOfferSize, }) => {
    (0, assert_1.default)(selectedOfferSize.purchasePriceCents <= originalOfferSize.purchasePriceCents, `Selected purchase price ${centsToDollars(selectedOfferSize.purchasePriceCents)} exceeds the underwritten purchase price ${centsToDollars(originalOfferSize.purchasePriceCents)}`);
    (0, assert_1.default)(selectedOfferSize.lineSizeCents <= originalOfferSize.lineSizeCents, `Selected line size ${centsToDollars(selectedOfferSize.lineSizeCents)} exceeds the underwritten line size ${centsToDollars(originalOfferSize.lineSizeCents)}`);
    (0, assert_1.default)(selectedOfferSize.avenPurchaseShareCents <= originalOfferSize.avenPurchaseShareCents, `Selected Aven purchase share ${centsToDollars(selectedOfferSize.avenPurchaseShareCents)} exceeds the underwritten Aven purchase share ${centsToDollars(originalOfferSize.avenPurchaseShareCents)}`);
    switch (amountToScale.method) {
        case homePurchaseTerms_1.CalculationMethod.fractionOfLineSize:
            return {
                method: amountToScale.method,
                fractionOfLineSize: amountToScale.fractionOfLineSize,
                amountAssumingOfferLineSize: centsToDollars(Math.round(selectedOfferSize.lineSizeCents * amountToScale.fractionOfLineSize)),
            };
        case homePurchaseTerms_1.CalculationMethod.fractionOfPurchasePrice:
            return {
                method: amountToScale.method,
                fractionOfPurchasePrice: amountToScale.fractionOfPurchasePrice,
                amountAssumingOfferPurchasePrice: centsToDollars(Math.round(selectedOfferSize.purchasePriceCents * amountToScale.fractionOfPurchasePrice)),
            };
        case homePurchaseTerms_1.CalculationMethod.fractionOfAvenPurchaseShare:
            return {
                method: amountToScale.method,
                fractionOfAvenPurchaseShare: amountToScale.fractionOfAvenPurchaseShare,
                amountAssumingOfferAvenPurchaseShare: centsToDollars(Math.round(selectedOfferSize.avenPurchaseShareCents * amountToScale.fractionOfAvenPurchaseShare)),
            };
        case homePurchaseTerms_1.CalculationMethod.fixedAmount:
            return amountToScale;
        default:
            return (0, assertUtils_1.assertNever)(amountToScale, `Unhandled CalculationMethod ${JSON.stringify(amountToScale)}`);
    }
};
exports.scaleAmountToSelectedOffer = scaleAmountToSelectedOffer;
// The underwritten line is read off the offer rather than recomputed as price less down payment: lineSize is
// the figure credit actually approved, and it is what every amount quoted against the line was quoted against.
const buildOfferSizeForScaling = ({ homeOffer }) => {
    const { netHomePurchaseAmount } = homeOffer.homePurchaseTerms;
    (0, assert_1.default)(netHomePurchaseAmount !== null, 'Cannot scale home purchase terms without netHomePurchaseAmount');
    return {
        lineSizeCents: dollarsToCents(homeOffer.lineSize),
        avenPurchaseShareCents: dollarsToCents(netHomePurchaseAmount),
        purchasePriceCents: dollarsToCents(homeOffer.homePurchaseTerms.purchasePrice),
    };
};
// No offer exists at the selected price, so its line and share are held at the same proportion of the price the
// underwritten offer struck — credit approved that ratio, and nothing re-underwrites it here.
const buildSelectedOfferSizeForScaling = ({ selectedPurchasePriceCents, originalOffer, selectedDownPaymentTerms, selectedCreditTerms, }) => {
    const originalPurchasePriceCents = dollarsToCents(originalOffer.homePurchaseTerms.purchasePrice);
    const selectedLineSizeCents = Math.round((dollarsToCents(originalOffer.lineSize) / originalPurchasePriceCents) * selectedPurchasePriceCents);
    const selectedDownPaymentCents = dollarsToCents((0, exports.getCalculatedAmountDollars)(selectedDownPaymentTerms));
    const selectedCreditsCents = selectedCreditTerms.reduce((totalCents, credit) => totalCents + dollarsToCents((0, exports.getCalculatedAmountDollars)(credit.creditAmount)), 0);
    const earnestMoneyDepositCents = dollarsToCents(originalOffer.homePurchaseTerms.earnestMoneyDeposit);
    const selectedAvenPurchaseShareCents = selectedPurchasePriceCents - selectedCreditsCents - selectedDownPaymentCents - earnestMoneyDepositCents;
    return { lineSizeCents: selectedLineSizeCents, avenPurchaseShareCents: selectedAvenPurchaseShareCents, purchasePriceCents: selectedPurchasePriceCents };
};
// annualInterestRate is carried through as it arrived: the rate does not move with the price, and re-emitting
// it would force this module to know both the live-class and JSON-wire shapes.
const scaleAvenSimpleLoanFinancingTermsToSelectedLine = ({ originalFinancingTerms, selectedAvenPurchaseShareCents, originalAvenPurchaseShareCents, }) => {
    const { financingTermsType, annualInterestRate, fixedTermMonths, monthlyPayment: originalMonthlyPayment } = originalFinancingTerms;
    const selectedMonthlyPaymentCents = Math.round((selectedAvenPurchaseShareCents / originalAvenPurchaseShareCents) * dollarsToCents(originalMonthlyPayment));
    return {
        financingTermsType,
        annualInterestRate,
        fixedTermMonths,
        monthlyPayment: centsToDollars(selectedMonthlyPaymentCents),
    };
};
//TODO Financing Terms should scale to loan amount, not aven purchase share (only the same when financed closing costs = 0)
const scaleFinancingTermsToSelectedLine = ({ originalFinancingTerms, selectedAvenPurchaseShareCents, originalAvenPurchaseShareCents, }) => {
    switch (originalFinancingTerms.financingTermsType) {
        case homePurchaseTerms_1.FinancingTermsType.avenSimpleLoan:
            return scaleAvenSimpleLoanFinancingTermsToSelectedLine({
                originalFinancingTerms: originalFinancingTerms,
                originalAvenPurchaseShareCents: originalAvenPurchaseShareCents,
                selectedAvenPurchaseShareCents: selectedAvenPurchaseShareCents,
            });
        default:
            return (0, assertUtils_1.assertNever)(originalFinancingTerms.financingTermsType, `Unhandled FinancingTermsType`);
    }
};
// A fee amount is a CalculatedAmount like any other, so each one tracks whichever offer figure its own method
// names — the same per-method rule the down payment follows. feeType, recipient and financingOption are terms
// of record and carry through untouched.
const scaleFeeTermsToSelectedPrice = ({ feesToScale, originalOfferSize, selectedOfferSize, }) => {
    return feesToScale.map((fee) => {
        return {
            ...fee,
            feeAmount: (0, exports.scaleAmountToSelectedOffer)({ amountToScale: fee.feeAmount, originalOfferSize, selectedOfferSize }),
        };
    });
};
const scaleCreditTermsToSelectedPrice = ({ creditsToScale, originalOfferSize, selectedOfferSize, }) => {
    return creditsToScale.map((credit) => {
        return {
            ...credit,
            creditAmount: (0, exports.scaleAmountToSelectedOffer)({ amountToScale: credit.creditAmount, originalOfferSize, selectedOfferSize }),
        };
    });
};
// Sum the disclosed fees into one total per financing option, so a caller can take just the slice it
// needs (borrower-paid-upfront fees for cash to close, financed fees for the loan amount, etc.). Every
// Sum disclosed fees by their settlement resolution. Every resolution appears in the result,
// including seller-paid rows that do not contribute to borrower cash to close.
const sumFeeDollarsByFinancingOption = (feeTerms) => {
    const totalDollarsByFinancingOption = {
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.borrowerPaidAtClosing]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.borrowerPaidBeforeClosing]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.sellerPaidAtClosing]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.sellerPaidBeforeClosing]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.lenderPaidBeforeClosingAbsorbed]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.lenderPaidAtClosingAbsorbed]: 0,
        [homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.lenderPaidAtClosingFinancedForBorrower]: 0,
    };
    for (const fee of feeTerms) {
        const financingOption = fee.financingOption.financingOptionType;
        totalDollarsByFinancingOption[financingOption] = (0, lodash_1.round)(totalDollarsByFinancingOption[financingOption] + (0, exports.getCalculatedAmountDollars)(fee.feeAmount), 2);
    }
    return totalDollarsByFinancingOption;
};
exports.sumFeeDollarsByFinancingOption = sumFeeDollarsByFinancingOption;
// The offer's purchase terms restated at a selected price. Pure — nothing is persisted.
const scaleHomePurchaseTermsToSelectedPrice = ({ selectedPurchasePriceDollars, originalHomePurchaseOffer, }) => {
    const originalHomePurchaseTerms = originalHomePurchaseOffer.homePurchaseTerms;
    (0, assert_1.default)(selectedPurchasePriceDollars > 0, `Selected purchase price must be a positive dollar amount, got ${selectedPurchasePriceDollars}`);
    const selectedPurchasePriceCents = dollarsToCents(selectedPurchasePriceDollars);
    const originalPurchasePriceCents = dollarsToCents(originalHomePurchaseTerms.purchasePrice);
    // The underwritten price is the ceiling: scaling up would quote a line/down payment above what was approved.
    (0, assert_1.default)(selectedPurchasePriceCents <= originalPurchasePriceCents, `Selected purchase price ${selectedPurchasePriceDollars} exceeds the underwritten price ${originalHomePurchaseTerms.purchasePrice}; the underwritten price is the ceiling`);
    if (selectedPurchasePriceCents === originalPurchasePriceCents) {
        return originalHomePurchaseOffer.homePurchaseTerms;
    }
    // TODO: Find ou how to get selected line size from the scaled selected purchase price, for now, we are assuming no borrower financed fees
    const originalOfferSize = buildOfferSizeForScaling({ homeOffer: originalHomePurchaseOffer });
    const provisionalSelectedOfferSize = {
        lineSizeCents: Math.round((dollarsToCents(originalHomePurchaseOffer.lineSize) / originalOfferSize.purchasePriceCents) * selectedPurchasePriceCents),
        avenPurchaseShareCents: Math.round((originalOfferSize.avenPurchaseShareCents / originalOfferSize.purchasePriceCents) * selectedPurchasePriceCents),
        purchasePriceCents: selectedPurchasePriceCents,
    };
    const selectedDownPaymentTerms = (0, exports.scaleAmountToSelectedOffer)({ amountToScale: originalHomePurchaseTerms.downPaymentTerms, originalOfferSize, selectedOfferSize: provisionalSelectedOfferSize });
    const selectedCreditTerms = scaleCreditTermsToSelectedPrice({ creditsToScale: originalHomePurchaseTerms.creditTerms, originalOfferSize, selectedOfferSize: provisionalSelectedOfferSize });
    const selectedOfferSize = buildSelectedOfferSizeForScaling({
        selectedPurchasePriceCents,
        originalOffer: originalHomePurchaseOffer,
        selectedDownPaymentTerms,
        selectedCreditTerms,
    });
    const selectedPurchaseFinancingTerms = scaleFinancingTermsToSelectedLine({
        originalFinancingTerms: originalHomePurchaseTerms.purchaseFinancingTerms,
        selectedAvenPurchaseShareCents: selectedOfferSize.avenPurchaseShareCents,
        originalAvenPurchaseShareCents: originalOfferSize.avenPurchaseShareCents,
    });
    const selectedFeeTerms = scaleFeeTermsToSelectedPrice({ feesToScale: originalHomePurchaseTerms.feeTerms, originalOfferSize, selectedOfferSize });
    // Cash to close = down payment + borrower-paid closing costs - base credits (mirrors underwriting).
    const requiredDownPayment = (0, exports.getCalculatedAmountDollars)(selectedDownPaymentTerms);
    const feeDollarsByFinancingOption = (0, exports.sumFeeDollarsByFinancingOption)(selectedFeeTerms);
    const borrowerPaidClosingCosts = feeDollarsByFinancingOption[homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.borrowerPaidAtClosing] + feeDollarsByFinancingOption[homePurchaseTerms_1.HomePurchaseFeeFinancingOptionType.borrowerPaidBeforeClosing];
    // TODO: split credits into base credits (reduce cash to close) and excess credits (reduce price).
    const baseCredits = selectedCreditTerms.reduce((total, credit) => (0, lodash_1.round)(total + (0, exports.getCalculatedAmountDollars)(credit.creditAmount), 2), 0);
    const selectedBorrowerCashToClose = (0, lodash_1.round)((0, lodash_1.round)(requiredDownPayment + borrowerPaidClosingCosts, 2) - baseCredits, 2);
    return {
        purchasePrice: selectedPurchasePriceDollars,
        loanAmount: originalHomePurchaseTerms.loanAmount,
        earnestMoneyDeposit: originalHomePurchaseTerms.earnestMoneyDeposit,
        netHomePurchaseAmount: centsToDollars(selectedOfferSize.avenPurchaseShareCents),
        homeOrHomeSearch: originalHomePurchaseTerms.homeOrHomeSearch,
        purchaseFinancingTerms: selectedPurchaseFinancingTerms,
        feeTerms: selectedFeeTerms,
        creditTerms: selectedCreditTerms,
        downPaymentTerms: selectedDownPaymentTerms,
        borrowerCashToClose: selectedBorrowerCashToClose,
    };
};
exports.scaleHomePurchaseTermsToSelectedPrice = scaleHomePurchaseTermsToSelectedPrice;
// Ceiling is the underwritten price. TODO: floor the slider at the cheapest price whose financed line still
// clears minLineSizeDollars — the parameter is accepted but not yet read, so today the floor is a flat 0.
const getHomePurchasePriceSliderBounds = ({ homePurchaseTerms }) => {
    return {
        floorDollars: 0,
        ceilingDollars: homePurchaseTerms.purchasePrice,
    };
};
exports.getHomePurchasePriceSliderBounds = getHomePurchasePriceSliderBounds;
